import { useSyncExternalStore } from 'react';

/**
 * Interface font picker. Per-device UI choice → localStorage, reactive via
 * useSyncExternalStore so the Settings control re-renders the whole app live.
 *
 * Mechanism: the body reads `font-family: var(--font-sans)` (index.css) and
 * Tailwind defines `--font-sans` as the Onest stack. We override that variable
 * inline on <html> for the chosen font — so switching back to Onest (or
 * clearing storage) restores the default with zero residue. Headings keep
 * their own `.font-display` (Onest) face; this only swaps the body/UI font.
 *
 * Onest ships eagerly in main.tsx; Golos Text lazy-loads its @fontsource
 * variable CSS on first selection so the initial bundle stays lean.
 */
export type FontId = 'onest' | 'golos-text';

interface FontOption {
  id: FontId;
  /** Short label for the Settings picker (brand name — not translated). */
  label: string;
  /** CSS font-family stack applied to `--font-sans`. */
  stack: string;
  /** Lazy-loads the @fontsource variable CSS. Omitted for the eager default. */
  load?: () => Promise<unknown>;
}

export const FONT_OPTIONS: readonly FontOption[] = [
  {
    id: 'onest',
    label: 'Onest',
    stack: '"Onest Variable", Onest, system-ui, sans-serif',
  },
  {
    id: 'golos-text',
    label: 'Golos Text',
    stack: '"Golos Text Variable", "Golos Text", system-ui, sans-serif',
    load: () => import('@fontsource-variable/golos-text'),
  },
];

const STORAGE_KEY = 'bunker.fontPreference.v1';
const DEFAULT: FontId = 'onest';

/** Coerce a stored/unknown value to a known font id (stale value → default). */
export function sanitizeFontId(raw: unknown): FontId {
  return FONT_OPTIONS.some((f) => f.id === raw) ? (raw as FontId) : DEFAULT;
}

function load(): FontId {
  try {
    return sanitizeFontId(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT;
  }
}

let current = load();
const listeners = new Set<() => void>();
const fontLoaded = new Set<FontId>(['onest']); // onest is eager (main.tsx)

/**
 * Monotonic counter of applyFont calls. The webfont import is awaited, so two
 * quick switches race: whichever import RESOLVES last wins, not whichever the
 * user picked last. Switching A→B could therefore leave A applied (and it made
 * fontPreference.test.ts flaky, a pending reset landing after the assertion).
 * Only the newest call is allowed to write the CSS var.
 */
let applyGeneration = 0;

export async function applyFont(id: FontId): Promise<void> {
  const generation = ++applyGeneration;
  const opt = FONT_OPTIONS.find((f) => f.id === id) ?? FONT_OPTIONS[0]!;
  if (opt.load && !fontLoaded.has(opt.id)) {
    try {
      await opt.load();
      fontLoaded.add(opt.id);
    } catch {
      // Webfont failed to load — the stack still falls back to system-ui, so
      // text stays readable rather than disappearing.
    }
  }
  // A newer choice arrived while this font was loading — that one owns the
  // CSS var now, so this stale application must not clobber it.
  if (generation !== applyGeneration) return;
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--font-sans', opt.stack);
  }
}

export function getFontPreference(): FontId {
  return current;
}

export function setFontPreference(id: FontId): void {
  const next = sanitizeFontId(id);
  if (next !== current) {
    current = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode / quota — session-only state still works */
    }
    for (const l of listeners) l();
  }
  void applyFont(next);
}

/** Apply the persisted font on boot. Call once at startup. */
export function hydrateFont(): void {
  void applyFont(current);
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useFontPreference(): FontId {
  return useSyncExternalStore(subscribe, () => current, () => current);
}
